const { logError, logWarn, logDebug } = require('./lib/logger');
const { loadScript, loadScriptWithin } = require('./lib/scriptLoader');
const { configurePrebid } = require('./lib/prebidConfig');
const { setupAutoRefresh } = require('./lib/autoRefresh');
const {
  applyContentAdSticky,
  clearContentAdSticky
} = require('./lib/contentAdSticky');
const { createPageAdRuntime } = require('./lib/adRuntime');
const {
  applyStableAdSlotSize,
  applySlotSizeFromPrebid,
  ensureAdSlotMinHeight,
  isMobileLbSlotId,
  isMobileLbAllowedSize,
  clampMobileLbContainer
} = require('./lib/adSizeUtils');
const { onElementInViewport } = require('./lib/viewportObserver');
const { resolveCoordinatedRequestThreshold } = require('./lib/adViewability');
const { ensurePrebidFallback, PREBID_LOAD_FAILSAFE_MS } = require('./lib/prebidFallback');
const { ensureProLinkStyles, buildProUpgradeUrl } = require('./lib/proLinks');
const { createNewsletterIframeModule } = require('./lib/newsletter');
const { createContestIframeModule } = require('./lib/contest');
const {
  createInsightsIframeModule,
  createPredictionIframeModule,
  ensureInsightsCompletionListener,
  extendArticleEndSlotDecision,
  fetchActiveEmbedItem,
  isInsightsFrequencyCapped,
  isPredictionFrequencyCapped,
  isSponsoredContent,
  listCompletedInsightsSurveyIds,
  readArticleTags,
  resolveInsightsArticleId,
  settleArticleEndSlot,
  shouldSuppressInsightSurveyEmbed,
  waitForArticleEndSlot,
  waitForStudyGroup
} = require('./lib/insights');
const { STUDY_GROUP_WAIT_MS } = require('./insightsStudyConfig');
const { setupMemberArticleAds } = require('./lib/memberAds');
const { injectStyles } = require('./lib/utils/styleInjector');
const { buildCdmPageOptions } = require('./lib/cdmAdtest');
const {
  createArticleInfiniteAdInserter,
  setupArticleInfiniteScrollAdsObserver
} = require('./lib/articleInfiniteAds');
const { ensureAdState } = require('./lib/adInsertion');
const {
  getArticleAdParagraphs,
  getArticleAdBoundary,
  resolveWordBasedArticleAdPlacements,
  resolveListicleAdPlacements,
  resolveUpperArticleAdPlacement
} = require('./lib/articleAdPlacement');
const {
  buildArticleGamTargeting,
  applyGamTargeting
} = require('./lib/gamTargeting');
const {
  applyPrebidTargetingToSlots,
  applySlotTargeting,
  configurePageSettings,
  enableLazyLoad
} = require('./lib/gptConfig');
const { createStoriesPlayer } = require('./lib/storiesPlayer');
const { initShowAwareArticle } = require('./lib/storiesPlayer/articlePlacement');
const { storiesAdOptions } = require('./lib/storiesPlayer/adOptions');

const {
  resolveLanguage,
  getProLinkText,
  buildRelatedSearchContainerHtml,
  getSlotPath,
  emitAdEvent
} = require('./adsBrandUtils');

// Ad unit factory for generating Prebid ad units
const {
  generateMobileArticleAdUnits,
  makeMobileContentAdUnit,
  makeMobileLbAdUnit,
  ensurePrebidAdUnitForCode
} = require('./adUnitFactory');

const INSTAGRAM_PLACEHOLDER_STYLES = `
.rm-embed-container.rm-embed-instagram {
  min-height: 420px;
}
@media (min-width: 768px) {
  .rm-embed-container.rm-embed-instagram {
    min-height: 520px;
  }
}
`;

function ensureInstagramEmbedPlaceholder() {
  try {
    injectStyles('milla-instagram-placeholder', INSTAGRAM_PLACEHOLDER_STYLES);
  } catch (_) {
    // ignore style injection failures
  }
}

function isBlockedEmbedParagraph(paragraph) {
  if (!paragraph || typeof paragraph.closest !== 'function') return false;
  return Boolean(
    paragraph.closest(
      '.rm-embed-container, .rm-shortcode, .rm-embed-holder, .rm-embed-spacer, iframe, .instagram-media'
    )
  );
}

function createArticleMobileAds(config, options = {}) {
  const targeting = options.targeting || {};
  const resultsPageBaseUrl = options.resultsPageBaseUrl || 'http://www.narcity.com/search/?afs&partner_param=param';
  const storiesPlayerConfig = options.storiesPlayer || {};

  return function initArticleMobileAds(ctx) {
    ensureInstagramEmbedPlaceholder();
    const {
      prebidUrl,
      gptUrl,
      _noads,
      _urlonly,
      _sponsored,
      _fr_lang,
      staticPage,
      fwjson,
      userIds,
      _googCsa,
      pbjs,
      googletag,
      pro,
      emitter,
      insights,
      auctionStrategy,
      adDiagnosticsEnabled,
      device,
      pageType,
      pageKey,
      walled
    } = ctx;

    const brand = config.brand;
    const language = resolveLanguage(config, _fr_lang);
    const proLinkText = getProLinkText(language);
    const proLinkUrl = buildProUpgradeUrl(config.proLinkSource.mobile);
    const storiesPlaylist = storiesPlayerConfig.playlist
      || (config.brand === 'mtlblog'
        ? 'mtlblog'
        : (language === 'fr' ? 'narcity_fr' : 'narcity_en'));
    const storiesPageTargeting = buildArticleGamTargeting({ fwjson, targeting, language });
    ensureProLinkStyles();
    const PREBID_TIMEOUT = 1500;
    const FAILSAFE_TIMEOUT = 3000;
    const SECONDS_TO_WAIT_AFTER_VIEWABILITY = 30;
    const storiesPlayer = createStoriesPlayer({
      showAware: storiesPlayerConfig.showAware === true,
      contentEnabled: storiesPlayerConfig.contentEnabled !== false,
      ...storiesAdOptions(config, ctx, storiesPlayerConfig, targeting, language),
      language,
      playlist: storiesPlaylist,
      manifestUrl: storiesPlayerConfig.manifestUrl,
      analyticsEndpoint: storiesPlayerConfig.analyticsEndpoint,
      containerSelector: '.stories-player-container',
      proLinkUrl,
      proLinkText,
      brand
    });
    initShowAwareArticle(storiesPlayer, storiesPlayerConfig, ctx, 'mobile');

    const mbContentSizes = [
      [300, 250],
      [336, 280],
      [300, 600],
      [320, 480],
      [320, 50],
      [320, 100],
      [1, 1]
    ];

    const mbUpperContentSizes = [
      [300, 250],
      [336, 280],
      [320, 480],
      [320, 50],
      [320, 100],
      [1, 1]
    ];

    const mbLeadSizes = [
      [320, 50],
      [300, 50]
    ];

    const CONTENT_SLOT_MIN_HEIGHT = 280;
    const LB_SLOT_MIN_HEIGHT = 50;
    const CONTENT_AD_PREFETCH_THRESHOLD = 3500;
    const CONTENT_AD_RENDER_FAILSAFE_MS = 4500;
    const ARTICLE_AD_WORD_INTERVAL = 250;
    const UPPER_ARTICLE_AD_WORD_THRESHOLD = 100;
    const UPPER_ARTICLE_SLOT_INDEX = 2;
    const UPPER_ARTICLE_SIZE_PROFILE = 'upper';
    const ARTICLE_AD_MAX_COUNT = 6;
    const INFINITE_SCROLL_AD_MIN_DISTANCE_PX = 2200;
    const INFINITE_SCROLL_AD_ITEM_INTERVAL = 4;

    const slotPrefix = config.slots.mobile.content;
    const lbSlotPrefix = config.slots.mobile.leaderboard;
    const gamPath = getSlotPath(config, 'mobile', 'content');
    const lbGamPath = getSlotPath(config, 'mobile', 'leaderboard');
    const mobileListicleConfig = config.articlePlacements?.mobileListicle || {};

    function getMobileContentSizes(slot) {
      return slot
        && slot.dataset
        && slot.dataset.millaAdSizeProfile === UPPER_ARTICLE_SIZE_PROFILE
        ? mbUpperContentSizes
        : mbContentSizes;
    }

    function renderDebugPlaceholders() {
      if (typeof window === 'undefined' || typeof document === 'undefined') {
        return;
      }
      if (window._millaShowAdPlaceholders !== true) {
        return;
      }

      const selectors = [
        `#${lbSlotPrefix}-1`,
        `#${lbSlotPrefix}-2`,
        '.content-ad'
      ];

    selectors.forEach((selector) => {
      const nodes = document.querySelectorAll(selector);
      nodes.forEach((div) => {
        if (!div) return;
        if (div.querySelector('.milla-ad-placeholder')) return;

        const hasRealChild = Array.prototype.some.call(div.childNodes || [], (node) => {
          if (node.nodeType !== 1) return false;
          return !node.classList || !node.classList.contains('milla-ad-placeholder');
        });
        if (hasRealChild) return;

        const placeholder = document.createElement('div');
        placeholder.className = 'milla-ad-placeholder';
        placeholder.style.minHeight = '100px';
        placeholder.style.background = '#f3f4f6';
        placeholder.style.border = '1px dashed #9ca3af';
        placeholder.style.display = 'flex';
        placeholder.style.alignItems = 'center';
        placeholder.style.justifyContent = 'center';
        placeholder.style.fontSize = '12px';
        placeholder.style.color = '#6b7280';
        placeholder.style.boxSizing = 'border-box';
        placeholder.textContent = 'Ad placeholder';
        div.appendChild(placeholder);
      });
    });
  }

    function primeContentSlotMinHeights() {
      if (typeof document === 'undefined') return;
      const slots = document.querySelectorAll('.content-ad');
      slots.forEach((slot) => {
        ensureAdSlotMinHeight(slot, CONTENT_SLOT_MIN_HEIGHT);
        prepareArticleAdSlot(slot);
      });
    }

    function isContentSlotId(slotId) {
      return typeof slotId === 'string' && slotId.indexOf(`${slotPrefix}-`) === 0;
    }

    function isTinyCreativeSize(size) {
      if (!size || !Array.isArray(size) || size.length < 2) return false;
      const width = Number(size[0]);
      const height = Number(size[1]);
      return Number.isFinite(width) && Number.isFinite(height) && width <= 1 && height <= 1;
    }

    function isCollapsibleTinyCreative(slotId, size) {
      return isTinyCreativeSize(size) && !hasPrebidWinningBid(slotId);
    }

    function setAdProLinksVisible(wrapper, visible) {
      if (!wrapper || typeof wrapper.querySelectorAll !== 'function') return;
      const links = wrapper.querySelectorAll('.requires-pro');
      links.forEach((link) => {
        if (link && link.style) {
          link.style.display = visible ? '' : 'none';
        }
      });
    }

    function getAdWrapper(slot) {
      if (!slot || typeof slot.closest !== 'function') return null;
      return slot.closest('.ad-wrapper');
    }

    function resetBoxStyles(node) {
      if (!node || !node.style) return;
      node.style.display = '';
      node.style.height = '';
      node.style.minHeight = '';
      node.style.width = '';
      node.style.minWidth = '';
      node.style.margin = '';
      node.style.padding = '';
      node.style.overflow = '';
      if (typeof node.removeAttribute === 'function') {
        node.removeAttribute('aria-hidden');
      }
    }

    function zeroBoxStyles(node, hide) {
      if (!node || !node.style) return;
      if (hide) {
        node.style.display = 'none';
      }
      node.style.height = '0px';
      node.style.minHeight = '0px';
      node.style.width = hide ? '0px' : '';
      node.style.minWidth = '0px';
      node.style.margin = '0';
      node.style.padding = '0';
      node.style.overflow = 'hidden';
      if (hide && typeof node.setAttribute === 'function') {
        node.setAttribute('aria-hidden', 'true');
      }
    }

    function prepareArticleAdSlot(slot) {
      if (!slot || !isContentSlotId(slot.id)) return;
      if (slot.dataset) {
        slot.dataset.millaAdState = 'pending';
      }
      const wrapper = getAdWrapper(slot);
      if (wrapper && wrapper.dataset) {
        wrapper.dataset.millaAdState = 'pending';
      }
      setAdProLinksVisible(wrapper, false);
    }

    function hasRenderedAdChild(slot) {
      if (!slot || !slot.childNodes) return false;
      return Array.prototype.some.call(slot.childNodes, (node) => {
        if (!node || node.nodeType !== 1) return false;
        return !node.classList || !node.classList.contains('milla-ad-placeholder');
      });
    }

    function clearArticleSlotFailsafe(slot) {
      if (!slot || !slot._millaRenderFailsafeTimer) return;
      clearTimeout(slot._millaRenderFailsafeTimer);
      slot._millaRenderFailsafeTimer = null;
    }

    function releaseArticleAdReservation(slot) {
      if (!slot || !isContentSlotId(slot.id)) return;
      clearContentAdSticky(slot);
      zeroBoxStyles(slot, false);
      const flex = typeof slot.closest === 'function' ? slot.closest('.ad-flex') : null;
      zeroBoxStyles(flex, false);
      const wrapper = getAdWrapper(slot);
      zeroBoxStyles(wrapper, false);
      setAdProLinksVisible(wrapper, false);
    }

    function restoreArticleAdSlot(slot) {
      if (!slot || !isContentSlotId(slot.id)) return;
      clearArticleSlotFailsafe(slot);
      resetBoxStyles(slot);
      const flex = typeof slot.closest === 'function' ? slot.closest('.ad-flex') : null;
      resetBoxStyles(flex);
      const wrapper = getAdWrapper(slot);
      resetBoxStyles(wrapper);
      if (slot.dataset) {
        slot.dataset.millaAdState = 'filled';
      }
      if (wrapper && wrapper.dataset) {
        wrapper.dataset.millaAdState = 'filled';
      }
      setAdProLinksVisible(wrapper, true);
    }

    function collapseDecorativeSibling(node) {
      if (!node || !node.classList) return;
      if (
        node.classList.contains('separator') ||
        (
          node.classList.contains('related-post') &&
          !(node.textContent || '').trim() &&
          (!node.children || node.children.length === 0)
        )
      ) {
        zeroBoxStyles(node, true);
      }
    }

    function collapseArticleAdSlot(slot) {
      if (!slot || !isContentSlotId(slot.id)) return;
      if (typeof window !== 'undefined' && window._millaShowAdPlaceholders === true) {
        return;
      }
      clearContentAdSticky(slot);
      clearArticleSlotFailsafe(slot);
      const wrapper = getAdWrapper(slot);
      if (slot.dataset) {
        slot.dataset.millaAdState = 'collapsed';
      }
      if (wrapper && wrapper.dataset) {
        wrapper.dataset.millaAdState = 'collapsed';
      }
      zeroBoxStyles(slot, true);
      const flex = typeof slot.closest === 'function' ? slot.closest('.ad-flex') : null;
      zeroBoxStyles(flex, true);
      zeroBoxStyles(wrapper, true);
      setAdProLinksVisible(wrapper, false);

      if (wrapper) {
        const prev = wrapper.previousElementSibling;
        collapseDecorativeSibling(prev);
        if (prev && prev.classList && prev.classList.contains('related-post')) {
          collapseDecorativeSibling(prev.previousElementSibling);
        }
        collapseDecorativeSibling(wrapper.nextElementSibling);
      }
    }

    function scheduleArticleSlotFailsafe(slotId) {
      if (
        typeof window === 'undefined' ||
        !isContentSlotId(slotId) ||
        window._millaShowAdPlaceholders === true
      ) {
        return;
      }
      const slot = document.getElementById(slotId);
      if (!slot) return;
      clearArticleSlotFailsafe(slot);
      slot._millaRenderFailsafeTimer = window.setTimeout(() => {
        const currentSlot = document.getElementById(slotId);
        if (!currentSlot) return;
        const state = currentSlot.dataset && currentSlot.dataset.millaAdState;
        if (state === 'filled' || state === 'collapsed') return;
        if (hasRenderedAdChild(currentSlot)) {
          restoreArticleAdSlot(currentSlot);
          return;
        }
        collapseArticleAdSlot(currentSlot);
      }, CONTENT_AD_RENDER_FAILSAFE_MS);
    }

    function collapseAllPendingArticleSlots() {
      if (typeof document === 'undefined') return;
      const slots = document.querySelectorAll(`.content-ad[id^="${slotPrefix}-"]`);
      slots.forEach((slot) => {
        if (!slot || (slot.dataset && slot.dataset.millaAdState === 'filled')) return;
        collapseArticleAdSlot(slot);
      });
    }

    function hasPrebidWinningBid(slotId) {
      if (!slotId || !pbjs || typeof pbjs.getAdserverTargetingForAdUnitCode !== 'function') {
        return false;
      }
      const targeting = pbjs.getAdserverTargetingForAdUnitCode(slotId) || {};
      return Boolean(
        targeting.hb_adid ||
        targeting.hb_bidder ||
        targeting.hb_pb ||
        targeting.hb_size
      );
    }

    const brandsnippetarticle = document.querySelector('.brandsnippet-article');

    if (_noads === 'yes' && brandsnippetarticle) {
      brandsnippetarticle.style.display = 'none';
    }

    if (_noads === 'yes' || _urlonly === 'yes' || pro === 'active') {
      emitAdEvent(emitter, brand, 'ready');
      return;
    }

    primeContentSlotMinHeights();

  // Inject an inline newsletter box in the article body for logged-out users on mobile
  if (
    typeof document !== 'undefined' &&
    !(_noads === 'yes' || _urlonly === 'yes' || _sponsored === 'yes' || pro === 'active') &&
    !(ctx && ctx.isLoggedIn === true) &&
    !walled
  ) {
    const getArticleContainer = () =>
      document.querySelector('.body-description') ||
      document.querySelector('article') ||
      document.querySelector('.article-body');

    const injectArticleEndCapture = () => {
      // The article-end slot is exclusive: yield to a survey/prediction/
      // contest embed when one renders there.
      waitForArticleEndSlot().then((occupied) => {
        if (occupied) return;
        try {
          const container = getArticleContainer();
          if (!container) {
            return;
          }

          const paragraphs = Array.from(container.querySelectorAll('p')).filter((p) => {
            if (!p || isBlockedEmbedParagraph(p)) return false;
            const text = (p.textContent || '').trim();
            return text.length > 0;
          });

          if (paragraphs.length === 0) {
            return;
          }

          const insertAfter = (targetNode, moduleEl) => {
            if (!targetNode || !targetNode.parentNode || !moduleEl) return;
            targetNode.parentNode.insertBefore(moduleEl, targetNode.nextSibling);
          };
          // Keep mobile article newsletter capture to a single article-end module.
          if (!container.querySelector('.inline-newsletter-box-end') && paragraphs.length >= 4) {
            const target = paragraphs[paragraphs.length - 1];
            const moduleEl = createNewsletterIframeModule('article_end');
            if (moduleEl) {
              moduleEl.classList.add('inline-newsletter-box-end');
              moduleEl.classList.add('milla-newsletter-embed');
              insertAfter(target, moduleEl);
            }
          }
        } catch (err) {
          logError('Failed to inject inline mobile newsletter box', err);
        }
      });
    };

    const injectInlineNewsletter = () => {
      try {
        const container = getArticleContainer();
        if (container && container.querySelector('.milla-newsletter-embed')) return;

        // Preferred placement: the compact newsletter unit directly above the
        // first in-body ad, where it renders regardless of what occupies the
        // exclusive article-end slot.
        const firstAdWrapper = container ? container.querySelector('.ad-wrapper') : null;
        if (firstAdWrapper) {
          const moduleEl = createNewsletterIframeModule('article_mid');
          if (!moduleEl) return;
          moduleEl.classList.add('inline-newsletter-box-mid');
          moduleEl.classList.add('milla-newsletter-embed');
          // Land above the ad's top separator so the unit doesn't wedge
          // between the separator rule and the ad it belongs to.
          const prev = firstAdWrapper.previousElementSibling;
          const anchor = prev && prev.classList && prev.classList.contains('separator')
            ? prev
            : firstAdWrapper;
          anchor.parentNode.insertBefore(moduleEl, anchor);
          return;
        }

        // Articles too short to carry an in-body ad fall back to the
        // article-end placement.
        injectArticleEndCapture();
      } catch (err) {
        logError('Failed to inject inline mobile newsletter box', err);
      }
    };

    // In-body ad wrappers are inserted synchronously later in this init, so
    // defer one tick to let the first ad placement exist before anchoring.
    const scheduleInlineNewsletter = () => {
      window.setTimeout(injectInlineNewsletter, 0);
    };

    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', scheduleInlineNewsletter);
    } else {
      scheduleInlineNewsletter();
    }
  }

  // Inject contest/insights widget at the bottom of the article body when active
  if (typeof document !== 'undefined') {
    const injectInsightsWidget = () => {
      try {
        const articleId = resolveInsightsArticleId();
        if (!articleId || staticPage || walled) {
          settleArticleEndSlot(false);
          return;
        }

        ensureInsightsCompletionListener();
        // A brand lift study reader names its study and group
        // (lib/insights/study.js). While this page's ads can still settle the
        // group, selection waits up to STUDY_GROUP_WAIT_MS and the
        // article-end decision is held for as long.
        const studyGroupReady = shouldSuppressInsightSurveyEmbed(ctx)
          ? Promise.resolve(null)
          : waitForStudyGroup(config.brand, STUDY_GROUP_WAIT_MS, { onWait: extendArticleEndSlotDecision });
        studyGroupReady
          .then((studyGroup) => fetchActiveEmbedItem(articleId, {
            brand: config.brand,
            language,
            tags: readArticleTags(ctx),
            sponsored: isSponsoredContent(ctx),
            excludeSurveyIds: listCompletedInsightsSurveyIds(config.brand),
            study: studyGroup ? studyGroup.studyKey : undefined,
            group: studyGroup ? studyGroup.group : undefined
          }).then((item) => ({ item, studyGroup })))
          .then(async ({ item, studyGroup }) => {
            if (!item || !item.payload) return false;
            const payload = item.payload;
            if (item.type !== 'contest' && shouldSuppressInsightSurveyEmbed(ctx)) return false;
            if (item.type === 'prediction' && isPredictionFrequencyCapped(config.brand, payload.id || payload._id)) return false;
            if (item.type === 'survey' && isInsightsFrequencyCapped(config.brand, payload.id || payload._id)) return false;

            const container =
              document.querySelector('.body-description') ||
              document.querySelector('article') ||
              document.querySelector('.article-body');
            if (!container || container.querySelector('.milla-contest-embed, .milla-insights-embed')) {
              return false;
            }

            const paragraphs = Array.from(container.querySelectorAll('p')).filter((p) => {
              if (!p || isBlockedEmbedParagraph(p)) return false;
              const text = (p.textContent || '').trim();
              return text.length > 0;
            });

            const insertAfter = (targetNode, moduleEl) => {
              if (!moduleEl) return;
              if (targetNode && targetNode.parentNode) {
                targetNode.parentNode.insertBefore(moduleEl, targetNode.nextSibling);
                return;
              }
              if (container && typeof container.appendChild === 'function') {
                container.appendChild(moduleEl);
              }
            };

            const anchor =
              container.querySelector('.inline-newsletter-box-end') ||
              (paragraphs.length ? paragraphs[paragraphs.length - 1] : null) ||
              container.lastElementChild;

            const moduleEl = item.type === 'contest'
              ? createContestIframeModule({
                  contestId: payload.id || payload._id,
                  articleId,
                  location: 'article_end'
                })
              : (item.type === 'prediction'
                ? await createPredictionIframeModule({
                    predictionId: payload.id || payload._id,
                    publicSlug: payload.publicSlug,
                    articleId,
                    brand: config.brand,
                    language
                  })
                : await createInsightsIframeModule({
                  surveyId: payload.id || payload._id,
                  surveyType: payload.type,
                  articleId,
                  location: 'article_end',
                  brand: config.brand,
                  language,
                  insights,
                  // The study survey's impression carries the exposure signal.
                  studyKey: studyGroup && payload.studyKey === studyGroup.studyKey
                    ? studyGroup.studyKey
                    : undefined
                }));
            if (moduleEl) {
              moduleEl.classList.add(item.type === 'contest' ? 'milla-contest-embed' : 'milla-insights-embed');
              insertAfter(anchor, moduleEl);
              return true;
            }
            return false;
          })
          .then((placed) => {
            settleArticleEndSlot(placed === true);
          })
          .catch((err) => {
            settleArticleEndSlot(false);
            logWarn('Failed to fetch active embed item', err);
          });
      } catch (err) {
        settleArticleEndSlot(false);
        logWarn('Failed to inject insights widget', err);
      }
    };

    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', injectInsightsWidget);
    } else {
      injectInsightsWidget();
    }
  }

  function initializeMobileAds() {
    const adRuntime = createPageAdRuntime({
      brand,
      pageType: pageType || 'article',
      device: device || 'mobile',
      pageKey,
      auctionStrategy,
      diagnosticsEnabled: adDiagnosticsEnabled,
      pbjs,
      googletag,
      timeoutMs: PREBID_TIMEOUT
    });
    const coordinator = adRuntime.coordinator;
    const contentAdRequestThreshold = resolveCoordinatedRequestThreshold({
      coordinated: adRuntime.coordinated,
      legacyThreshold: CONTENT_AD_PREFETCH_THRESHOLD,
      device: device || 'mobile'
    });
    const upperSlot = document.getElementById(`${slotPrefix}-${UPPER_ARTICLE_SLOT_INDEX}`);
    const hasUpperArticleSlot = getMobileContentSizes(upperSlot) === mbUpperContentSizes;
    // Generate ad units using factory with article-specific sizes
    const adUnits = adRuntime.coordinated
      ? []
      : generateMobileArticleAdUnits(config, {
        leaderboardCount: 2,
        contentCount: 15,
        leaderboardSizes: mbLeadSizes,
        contentSizes: mbContentSizes,
        contentSizesByIndex: hasUpperArticleSlot
          ? { [UPPER_ARTICLE_SLOT_INDEX]: mbUpperContentSizes }
          : undefined
      });

    pbjs.que.push(function () {
      configurePrebid(pbjs, userIds);
      if (adRuntime.coordinated) {
        return;
      }
      pbjs.addAdUnits(adUnits);
      pbjs.requestBids({
        timeout: PREBID_TIMEOUT,
        bidsBackHandler: function () {
          initAdserver();
        }
      });
    });

    if (typeof window !== 'undefined' && window._millaShowAdPlaceholders === true) {
      setTimeout(renderDebugPlaceholders, 3000);
    }

    function initAdserver() {
      if (!pbjs || typeof pbjs.getBidResponses !== 'function' || pbjs.initAdserverSet) {
        return;
      }
      pbjs.initAdserverSet = true;
    }

    if (!adRuntime.coordinated) {
      setTimeout(initAdserver, FAILSAFE_TIMEOUT);
    }

    googletag.cmd.push(function () {
      const REFRESH_KEY = 'refresh';
      const REFRESH_VALUE = 'true';
      adRuntime.observeGpt(googletag.pubads());

      if (!adRuntime.coordinated) {
        enableLazyLoad(googletag, googletag.pubads(), {
          fetchMarginPercent: 200,
          renderMarginPercent: 100,
          mobileScaling: 1.5
        });
      }

      googletag
        .pubads()
        .addEventListener('slotRenderEnded', function (event) {
          const slot = event.slot;
          const div = document.getElementById(slot.getSlotElementId());
          const size = event.size;
          const slotId = slot.getSlotElementId();
          const shouldCollapseContentSlot =
            div &&
            isContentSlotId(slotId) &&
            !(typeof window !== 'undefined' && window._millaShowAdPlaceholders === true) &&
            (event.isEmpty || isCollapsibleTinyCreative(slotId, size));

          if (shouldCollapseContentSlot) {
            collapseArticleAdSlot(div);
            return;
          }

          if (div && isContentSlotId(slotId)) {
            restoreArticleAdSlot(div);
          }

          if (size && size.length === 2 && div) {
            if (isMobileLbSlotId(slotId) && !isMobileLbAllowedSize(size[0], size[1])) {
              logWarn('mobile-lb oversized creative rejected', { slotId, size });
              clampMobileLbContainer(div);
              const iframe = div.querySelector('iframe');
              if (iframe) iframe.style.display = 'none';
            } else {
              applyStableAdSlotSize(div, size);
            }
          }
          if (!event.isEmpty && div && isContentSlotId(slotId)) {
            applyContentAdSticky(div, size);
          }
          // Reset non-article containers if GPT returned no ad.
          if (event.isEmpty && div) {
            div.style.height = 'auto';
          }
          // Optional debug: show placeholders where slots finish empty.
          if (
            typeof window !== 'undefined' &&
            window._millaShowAdPlaceholders === true &&
            div
          ) {
            const existingPlaceholder = div.querySelector('.milla-ad-placeholder');
            if (event.isEmpty) {
              if (div.style && div.style.display === 'none') {
                div.style.display = '';
              }
              if (!existingPlaceholder) {
                const placeholder = document.createElement('div');
                placeholder.className = 'milla-ad-placeholder';
                placeholder.style.minHeight = '100px';
                placeholder.style.background = '#f3f4f6';
                placeholder.style.border = '1px dashed #9ca3af';
                placeholder.style.display = 'flex';
                placeholder.style.alignItems = 'center';
                placeholder.style.justifyContent = 'center';
                placeholder.style.fontSize = '12px';
                placeholder.style.color = '#6b7280';
                placeholder.style.boxSizing = 'border-box';
                placeholder.textContent = 'Ad placeholder';
                div.appendChild(placeholder);
              }
            } else if (existingPlaceholder && existingPlaceholder.parentNode === div) {
              existingPlaceholder.parentNode.removeChild(existingPlaceholder);
            }
          }
        });

      applyGamTargeting(googletag, storiesPageTargeting);

      // PPID = the derived, consent-gated ads id only (2026-09-09); no sharedId fallback.
      if (window.ppid) {
        googletag.pubads().setPublisherProvidedId(window.ppid);
      }

      configurePageSettings(googletag, googletag.pubads(), {
        disableInitialLoad: true,
        singleRequest: true,
        collapseDiv:
          typeof window !== 'undefined' && window._millaShowAdPlaceholders === true
            ? null
            : 'ON_NO_FILL'
      });
      googletag.enableServices();

      setupAutoRefresh(googletag, pbjs, {
        refreshKey: REFRESH_KEY,
        refreshValue: REFRESH_VALUE,
        prebidTimeout: PREBID_TIMEOUT,
        emptyInitialDelayMs: adRuntime.coordinated ? 15000 : undefined,
        emptyDelayMs: adRuntime.coordinated ? 30000 : 10000,
        filledDelayMs: SECONDS_TO_WAIT_AFTER_VIEWABILITY * 1000,
        viewabilityThreshold: 0.5,
        viewabilityTimeMs: 1000,
        maxRefreshes: 10,
        maxEmptyRefreshes: adRuntime.coordinated ? 2 : undefined,
        maxFilledRefreshes: adRuntime.coordinated ? 10 : undefined,
        preloadMarginPx: adRuntime.coordinated ? 0 : undefined,
        coordinator,
        shouldManageSlot: function (slotId) {
          return (
            slotId.indexOf(lbSlotPrefix) !== -1 ||
            slotId.indexOf(slotPrefix) !== -1
          );
        }
      });

      if (_sponsored !== 'yes') {
        const brandSnippetId = `brandsnippet-${brand}`;
        const brandSnippetSlot = applySlotTargeting(
          googletag.defineSlot(
            getSlotPath(config, 'desktop', 'brandSnippet'),
            ['fluid'],
            brandSnippetId
          ),
          { lazyload: 'lazyload' }
        ).addService(googletag.pubads());
        googletag.display(brandSnippetId);
        googletag.pubads().refresh([brandSnippetSlot]);
      }

      const mobileLbSlot1 = applySlotTargeting(
        googletag.defineSlot(lbGamPath, mbLeadSizes, `${lbSlotPrefix}-1`),
        {
          lazyload: 'lazyload',
          refresh: 'true'
        }
      ).addService(googletag.pubads());

      const mobileLbSlot2 = applySlotTargeting(
        googletag.defineSlot(lbGamPath, mbLeadSizes, `${lbSlotPrefix}-2`),
        {
          lazyload: 'lazyload',
          refresh: 'true'
        }
      ).addService(googletag.pubads());

      // Display floating banner - required for disableInitialLoad
      googletag.display(`${lbSlotPrefix}-2`);

      if (coordinator) {
        coordinator.registerSlot({
          slotId: `${lbSlotPrefix}-1`,
          gptSlot: mobileLbSlot1,
          createAdUnit: () => makeMobileLbAdUnit(1, config, { sizes: mbLeadSizes }),
          placement: { slotFamily: 'leaderboard', slotIndex: 1 }
        });
        coordinator.registerSlot({
          slotId: `${lbSlotPrefix}-2`,
          gptSlot: mobileLbSlot2,
          createAdUnit: () => makeMobileLbAdUnit(2, config, { sizes: mbLeadSizes }),
          placement: { slotFamily: 'leaderboard', slotIndex: 2 }
        });
        coordinator.requestInitial([`${lbSlotPrefix}-1`, `${lbSlotPrefix}-2`]);
      } else {
        pbjs.requestBids({
          timeout: PREBID_TIMEOUT,
          adUnitCodes: [`${lbSlotPrefix}-1`, `${lbSlotPrefix}-2`],
          bidsBackHandler: function () {
            applySlotSizeFromPrebid(`${lbSlotPrefix}-1`, pbjs);
            applySlotSizeFromPrebid(`${lbSlotPrefix}-2`, pbjs);
            applyPrebidTargetingToSlots(pbjs, [mobileLbSlot1, mobileLbSlot2]);
            googletag.pubads().refresh([mobileLbSlot1, mobileLbSlot2]);
          }
        });
      }

      onElementInViewport({
        selector: '.content-ad',
        onIntersect: function (entry) {
          const slotId = entry && entry.target && entry.target.id;
          if (!slotId) return;
          const slotSizes = getMobileContentSizes(entry.target);
          logDebug('slot:define', {
            slotId,
            adUnit: gamPath
          });
          const adSlot = applySlotTargeting(
            googletag.defineSlot(
              gamPath,
              slotSizes,
              slotId
            ),
            {
              lazyload: 'lazyload',
              [REFRESH_KEY]: REFRESH_VALUE
            }
          ).addService(googletag.pubads());
          logDebug('slot:display', { slotId });
          googletag.display(slotId);

          logDebug('slot:requestBids', { slotId, timeout: PREBID_TIMEOUT });
          if (coordinator) {
            const slotIndex = Number((slotId.match(/-(\d+)$/) || [])[1]);
            coordinator.registerSlot({
              slotId,
              gptSlot: adSlot,
              createAdUnit: () => makeMobileContentAdUnit(slotIndex, config, { sizes: slotSizes }),
              placement: { slotFamily: 'content', slotIndex }
            });
            coordinator.requestInitial(slotId).then((result) => {
              if (result.status === 'no_bid') {
                logDebug('slot:noPrebidBid', { slotId });
                releaseArticleAdReservation(document.getElementById(slotId));
              }
              scheduleArticleSlotFailsafe(slotId);
            });
          } else {
            ensurePrebidAdUnitForCode(pbjs, slotId, config, 'mobile');
            pbjs.requestBids({
              timeout: PREBID_TIMEOUT,
              adUnitCodes: [slotId],
              bidsBackHandler: function () {
                googletag.cmd.push(function () {
                  logDebug('slot:bidsBack', { slotId });
                  if (!hasPrebidWinningBid(slotId)) {
                    logDebug('slot:noPrebidBid', { slotId });
                    releaseArticleAdReservation(document.getElementById(slotId));
                  } else {
                    applySlotSizeFromPrebid(slotId, pbjs);
                  }
                  applyPrebidTargetingToSlots(pbjs, adSlot);
                  logDebug('slot:refresh', { slotId, reason: 'initial' });
                  scheduleArticleSlotFailsafe(slotId);
                  googletag.pubads().refresh([adSlot]);
                });
              }
            });
          }
        },
        threshold: contentAdRequestThreshold,
        oneTime: true
      });
    });
  }

  // CDM container for articles — fires immediately on page load, no viewability wait
  if (
    typeof document !== 'undefined' &&
    typeof _googCsa === 'function' &&
    !(_noads === 'yes' || _urlonly === 'yes' || pro === 'active' || _sponsored === 'yes' || staticPage || walled)
  ) {
    const initArticleCdm = () => {
      const articleCsaConfig = config.articleCsa || null;
      if (!articleCsaConfig) return;
      const cdmContainerId = articleCsaConfig.container || 'cdm-container-article';
      let cdmContainer = document.getElementById(cdmContainerId);
      if (!cdmContainer) {
        cdmContainer = document.createElement('div');
        cdmContainer.id = cdmContainerId;
        cdmContainer.className = 'article-cdm-container';
        cdmContainer.style.margin = '24px 0';
        cdmContainer.style.width = '100%';
        const articleBody = document.querySelector('.body-description')
          || document.querySelector('.article-body');
        if (articleBody && articleBody.parentElement) {
          articleBody.parentElement.insertBefore(cdmContainer, articleBody.nextSibling);
        }
      }
      if (cdmContainer) {
        try {
          _googCsa('cdm', buildCdmPageOptions(articleCsaConfig), { container: cdmContainerId });
        } catch (err) {
          logWarn('Failed to initialize article CDM block (mobile)', err);
        }
      }
    };
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', initArticleCdm);
    } else {
      initArticleCdm();
    }
  }

  loadScript(gptUrl)
    .then(() => {
      return loadScriptWithin(prebidUrl, PREBID_LOAD_FAILSAFE_MS)
        .then(() => {
          initializeMobileAds();
        })
        .catch((err) => {
          logWarn('Prebid failed to load; continuing with GPT-only ads', err);
          ensurePrebidFallback(pbjs);
          initializeMobileAds();
        });
    })
    .catch((err) => {
      logError('Failed to load GPT script (mobile)', err);
      collapseAllPendingArticleSlots();
      emitAdEvent(emitter, brand, 'error', err);
    });

  const widgetHead = document.querySelector('.widget__head');
  if (widgetHead) {
    widgetHead.insertAdjacentHTML(
      'afterend',
      `<div class="mobile-lb-placeholder"><div id="${lbSlotPrefix}-1" class="mobile-lb-ad"></div></div>`
    );
  }
  ensureAdSlotMinHeight(
    document.getElementById(`${lbSlotPrefix}-1`),
    LB_SLOT_MIN_HEIGHT
  );
  clampMobileLbContainer(document.getElementById(`${lbSlotPrefix}-1`));

  // Inject mobile floating banner for leaderboard slot 2
  if (!document.querySelector('.floating-banner')) {
    document.body.insertAdjacentHTML(
      'beforeend',
      `<div class="floating-banner" data-value="btest-324"><div id="${lbSlotPrefix}-2" class="banner-content"></div></div>`
    );
  }
  ensureAdSlotMinHeight(
    document.getElementById(`${lbSlotPrefix}-2`),
    LB_SLOT_MIN_HEIGHT
  );
  clampMobileLbContainer(document.getElementById(`${lbSlotPrefix}-2`));

  ensureAdState({ trendingAdsInserted: 0 });

  if (_sponsored !== 'yes') {
    function ensureStoriesContainerFallback() {
      if (typeof document === 'undefined') {
        return;
      }

      const insertIfReady = () => {
        const container =
          document.querySelector('.body-description')
          || document.querySelector('article')
          || document.querySelector('.article-body');
        if (!container) {
          return false;
        }

        if (container.querySelector('.stories-player-container')) {
          return true;
        }

        const paragraphs = Array.from(container.querySelectorAll('p')).filter((p) => {
          if (!p || isBlockedEmbedParagraph(p)) return false;
          const text = (p.textContent || '').trim();
          return text.length > 0;
        });
        let target = paragraphs[0] || container.firstElementChild || container;
        if (paragraphs.length > 0) {
          let wordsSeen = 0;
          for (let i = 0; i < paragraphs.length; i += 1) {
            const text = (paragraphs[i].innerText || paragraphs[i].textContent || '').trim();
            wordsSeen += text ? text.split(/\s+/).filter(Boolean).length : 0;
            target = paragraphs[i];
            if (wordsSeen >= 100) {
              break;
            }
          }
        }

        const storiesContainerHTML = ''
          + '<div class="stories-player-container stories-player-container--fallback" style="padding:15px;background:#F9F9F9;box-sizing:border-box;">'
          + '<a href="' + proLinkUrl + '" class="requires-pro milla-pro-link" target="_blank" rel="noopener noreferrer">' + proLinkText + '</a>'
          + '</div>';

        const parentElem = target.parentElement;
        let inserted = false;
        if (parentElem && parentElem.classList.contains('body-description')) {
          target.insertAdjacentHTML('afterend', storiesContainerHTML);
          inserted = true;
        } else if (parentElem && parentElem.classList.contains('rebellt-item')) {
          parentElem.insertAdjacentHTML('beforeBegin', storiesContainerHTML);
          inserted = true;
        } else if (parentElem && parentElem.closest('.rebellt-item')) {
          parentElem.closest('.rebellt-item').insertAdjacentHTML('beforeBegin', storiesContainerHTML);
          inserted = true;
        } else if (parentElem && parentElem.closest('.rm-shortcode')) {
          parentElem.closest('.rm-shortcode').insertAdjacentHTML('beforeBegin', storiesContainerHTML);
          inserted = true;
        }

        if (!inserted) {
          if (target && target !== container && typeof target.insertAdjacentHTML === 'function') {
            target.insertAdjacentHTML('afterend', storiesContainerHTML);
          } else if (typeof container.insertAdjacentHTML === 'function') {
            container.insertAdjacentHTML('afterbegin', storiesContainerHTML);
          }
        }

        return true;
      };

      if (insertIfReady()) {
        return;
      }

      if (
        typeof window === 'undefined'
        || typeof MutationObserver === 'undefined'
        || window.__millaStoriesFallbackObserverActive
      ) {
        return;
      }

      const root = document.documentElement || document.body;
      if (!root) {
        return;
      }

      const observer = new MutationObserver(() => {
        if (insertIfReady()) {
          observer.disconnect();
          window.__millaStoriesFallbackObserverActive = false;
        }
      });

      observer.observe(root, { childList: true, subtree: true });
      window.__millaStoriesFallbackObserverActive = true;

      setTimeout(() => {
        if (window.__millaStoriesFallbackObserverActive) {
          observer.disconnect();
          window.__millaStoriesFallbackObserverActive = false;
        }
      }, 15000);
    }

    function prepareInsertedSlots(slotIds) {
      const ids = Array.isArray(slotIds) ? slotIds : (slotIds ? [slotIds] : []);
      ids.forEach((slotId) => {
        if (!slotId) return;
        const slot = document.getElementById(slotId);
        ensureAdSlotMinHeight(slot, CONTENT_SLOT_MIN_HEIGHT);
        prepareArticleAdSlot(slot);
      });
    }

    function insertAd(element, adHTML, slotIds) {
      if (!element || !element.parentElement) return false;
      const next = element.nextElementSibling;
      if (next && next.matches('.separator, .stories-player-container, .ad-wrapper, .related-post')) {
        return false;
      }
      element.insertAdjacentHTML('afterend', adHTML);
      prepareInsertedSlots(slotIds);
      return true;
    }

    function buildMobileArticleAdHtml(adNumber, slotId) {
      const topSeparator = '<div class="separator"><div class="separator-top"></div></div>';
      const bottomSeparator = '<div class="separator"><div class="separator-bottom"></div></div>';
      const proLink = '<a href="' + proLinkUrl + '" class="requires-pro milla-pro-link" target="_blank" rel="noopener noreferrer">' + proLinkText + '</a>';
      const adWrapper = '<div class="ad-wrapper"><div class="ad-flex">'
        + proLink
        + `<div id="${slotId}" class="content-ad"></div>`
        + '</div></div>';

      if (adNumber === 1) {
        const storiesContainer = '<div class="stories-player-container" style="padding:15px;background:#F9F9F9;box-sizing:border-box;">'
          + proLink
          + '</div>';
        return topSeparator + adWrapper + storiesContainer + bottomSeparator;
      }
      if (adNumber === 2) {
        return topSeparator
          + buildRelatedSearchContainerHtml()
          + adWrapper
          + bottomSeparator;
      }
      return topSeparator
        + '<div class="related-post"></div>'
        + adWrapper
        + bottomSeparator;
    }

    function buildUpperMobileArticleAdHtml(slotId) {
      const proLink = '<a href="' + proLinkUrl + '" class="requires-pro milla-pro-link" target="_blank" rel="noopener noreferrer">' + proLinkText + '</a>';
      return '<div class="separator"><div class="separator-top"></div></div>'
        + '<div class="ad-wrapper"><div class="ad-flex">'
        + proLink
        + `<div id="${slotId}" class="content-ad" data-milla-ad-size-profile="${UPPER_ARTICLE_SIZE_PROFILE}"></div>`
        + '</div></div>'
        + '<div class="separator"><div class="separator-bottom"></div></div>';
    }

    function insertListicleAwareAds() {
      if (mobileListicleConfig.enabled !== true) return 0;
      const articleBody = document.querySelector('.body-description');
      if (!articleBody) return 0;
      const placements = resolveListicleAdPlacements(articleBody, mobileListicleConfig);
      if (placements.length === 0) return 0;

      placements.forEach((placement) => {
        const slotId = `${slotPrefix}-${placement.slotIndex}`;
        if (document.getElementById(slotId)) return;
        placement.anchor.insertAdjacentHTML(
          'afterend',
          buildMobileArticleAdHtml(placement.slotIndex, slotId)
        );
        prepareInsertedSlots(slotId);
      });

      const highestSlotIndex = placements[placements.length - 1].slotIndex;
      const currentPlacedCount = Number(window.adData.placedAdCount);
      window.adData.placedAdCount = Math.max(
        Number.isFinite(currentPlacedCount) ? currentPlacedCount : 0,
        highestSlotIndex
      );
      return placements.length;
    }

    // A walled view requests nothing in the body (plans/dynamic-login-wall.md).
    const insertedListicleAdCount = walled ? 0 : insertListicleAwareAds();
    const articleBody = document.querySelector('.body-description');

    if (insertedListicleAdCount === 0 && !walled) {
      const placements = resolveWordBasedArticleAdPlacements(articleBody, {
        wordInterval: ARTICLE_AD_WORD_INTERVAL,
        maxCount: ARTICLE_AD_MAX_COUNT
      });
      const pElements = getArticleAdParagraphs(articleBody).filter((paragraph) => (
        getArticleAdBoundary(paragraph, articleBody) === paragraph
      ));
      const upperPlacement = resolveUpperArticleAdPlacement(pElements, {
        upperWordThreshold: UPPER_ARTICLE_AD_WORD_THRESHOLD,
        existingFirstAdWordThreshold: ARTICLE_AD_WORD_INTERVAL,
        minParagraphGap: 2
      });
      // Slot 2 was already skipped by the display + Stories placement. Reusing
      // it preserves the historical slot-1 and later display reporting paths.
      const upperSlotId = `${slotPrefix}-${UPPER_ARTICLE_SLOT_INDEX}`;
      const upperPrecedesFirstAd = upperPlacement && placements[0]
        && (upperPlacement.anchor.compareDocumentPosition(placements[0].anchor) & 4);
      if (upperPrecedesFirstAd && !document.getElementById(upperSlotId)) {
        insertAd(
          upperPlacement.anchor,
          buildUpperMobileArticleAdHtml(upperSlotId),
          upperSlotId
        );
      }

      placements.forEach((placement, index) => {
        // Preserve slot 1 for display + Stories and slot 2 for the upper unit.
        const slotIndex = index === 0 ? 1 : index + 2;
        const slotId = `${slotPrefix}-${slotIndex}`;
        if (document.getElementById(slotId)) return;
        if (!insertAd(placement.anchor, buildMobileArticleAdHtml(index + 1, slotId), slotId)) return;
        window.adData.placedAdCount = Math.max(window.adData.placedAdCount, slotIndex + 1, 3);
      });
    }

    const articleInfiniteAdOptions = {
      slotPrefix,
      proLinkUrl,
      proLinkText,
      minHeight: CONTENT_SLOT_MIN_HEIGHT,
      minDistancePx: INFINITE_SCROLL_AD_MIN_DISTANCE_PX,
      itemInterval: INFINITE_SCROLL_AD_ITEM_INTERVAL,
      prepareSlot: prepareArticleAdSlot
    };
    window.insertNewAd = createArticleInfiniteAdInserter(articleInfiniteAdOptions);
    setupArticleInfiniteScrollAdsObserver(articleInfiniteAdOptions);

    const pageOptions = {
      pubId: 'partner-pub-7289986524496939',
      styleId: '7477868506',
      relatedSearchTargeting: 'content',
      resultsPageBaseUrl,
      resultsPageQueryParam: 'q'
    };

    const rsblock1 = {
      container: 'relatedsearches1',
      relatedSearches: 5
    };

    if (!walled) {
      ensureStoriesContainerFallback();
      if (!storiesPlayerConfig.showAware) storiesPlayer(ctx);

      setupMemberArticleAds(ctx, {
        device: 'mobile',
        slotPrefix,
        minHeight: CONTENT_SLOT_MIN_HEIGHT,
        prepareSlot: prepareArticleAdSlot
      });

      if (typeof _googCsa === 'function') {
        _googCsa('relatedsearch', pageOptions, rsblock1);
      }
    }

  }

    emitAdEvent(emitter, brand, 'ready');
  };
}

module.exports = {
  createArticleMobileAds
};
